import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { money, METAL_SWATCH } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Stars } from "@/components/common";
import { toast } from "sonner";
import { editionInfo } from "@/components/EditionInfo";

export function ProductCard({ p, index = 0 }: { p: any; index?: number }) {
  const { wishlist, toggleWish, user } = useAuth();
  const nav = useNavigate();
  const [hover, setHover] = useState(false);
  const saved = wishlist.includes(p.id);
  const img = hover && p.images?.[1] ? p.images[1] : p.image;
  const ed = editionInfo(p);
  const badge = p.cms?.badge || ed?.label || (p.tags?.includes("New") ? "New" : "");

  const onWish = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!user) { toast("Sign in to save pieces to your wishlist"); nav("/login"); return; }
    await toggleWish(p.id);
    toast(saved ? "Removed from wishlist" : "Saved to wishlist");
  };

  return (
    <Link to={`/products/${p.slug}`} className="group block rise" style={{ animationDelay: `${(index % 8) * 60}ms` }}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} data-testid={`product-card-${p.slug}`}>
      <div className="relative aspect-square bg-white overflow-hidden border border-transparent group-hover:border-neutral-200 transition-colors">
        <img src={img} alt={p.name} loading="lazy" className="w-full h-full object-contain p-4 transition-transform duration-700 group-hover:scale-105" />
        {badge && <span className="absolute top-3 left-3 max-w-[calc(100%-4.25rem)] leading-tight eyebrow !text-[9px] md:!text-[10px] !tracking-[0.12em] md:!tracking-[0.2em] bg-[var(--c-bg)] px-2 py-1" title={badge} data-testid="product-badge">{badge}</span>}
        <button onClick={onWish} aria-label="Save to wishlist" data-testid={`wishlist-toggle-${p.slug}`}
          className="absolute top-3 right-3 h-9 w-9 grid place-items-center bg-white/80 backdrop-blur hover:bg-white transition-colors">
          <Heart size={16} strokeWidth={1.4} className={saved ? "fill-[var(--c-primary)] text-[var(--c-primary)]" : ""} />
        </button>
      </div>
      <div className="pt-4 space-y-1.5">
        <div className="flex gap-1.5">
          {(p.metals || []).slice(0, 4).map((m: string) => (
            <span key={m} title={m} className="h-3 w-3 rounded-full border border-neutral-300" style={{ background: METAL_SWATCH[m] || "#ddd" }} />
          ))}
        </div>
        <h3 className="font-display text-lg leading-snug group-hover:text-[var(--c-primary)] transition-colors">{p.name}</h3>
        <div className="flex items-center justify-between">
          <p className="text-sm" data-testid="product-card-price">{money(p.price)}</p>
          {p.review_count > 0 && <Stars value={p.rating} size={11} />}
        </div>
        {ed?.note && <p className="text-[11px] tracking-wide text-[var(--c-primary)]" data-testid="product-card-edition-note">{ed.note}</p>}
      </div>
    </Link>
  );
}
